// ABOUTME: Main entry point for Hopp — WM 2026 fan pennant iFrame
// ABOUTME: Boots Three.js scene, pennant rendering, polling, and tap-and-hold flow

import * as THREE from 'three';
import { loadConfig, getOnboardingConfig, getPennantsConfig, getInteractionConfig, getConfig } from './config.js';
import {
    initApiClient,
    fetchRecentPennants,
    fetchStats,
    initializeKnownPennants,
    subscribeToPennants,
    createPennant,
} from './api-client.js';
import { loadTeams, getTeam, getAllTeams } from './teams.js';
import {
    createPennantSystem,
    addPennant,
    renamePennantId,
    updateFlutter,
    setFlutterIntensity,
    setPennantSunDirection,
    setPennantSwingBoost,
    setPennantPoleHeightScale,
    setFreshGlowWindow,
    updatePennantMatrices,
    hidePennant,
    hexToRgb,
    PENNANT_FLAG_FOCUS_Y,
} from './pennant-mesh.js';
import { buildFlagAtlas } from './flag-atlas.js';
import { generateSimulatedPennants, parsePennantSimulationParams } from './pennant-simulation.js';
import { createTeamCounts, incrementTeamCount, summarizeTeamCounts, resolveDisplayedCount } from './pennant-stats.js';
import {
    createFieldScene,
    createFieldFog,
    setFieldSunDirection,
    setFieldCelestialState,
    getCelestialState,
    getPitchMesh,
    getSunDirection,
    getInitialCameraPose,
    INITIAL_CAMERA_FOV_DEG,
} from './field-scene.js';
import { createGrassBillboards, updateGrassWind, setGrassLighting } from './grass-billboards.js';
import { screenToWorld, isSessionPlanted as _isSessionPlanted, markSessionPlanted as _markSessionPlanted, clearSessionPlanted } from './interaction.js';
import { resolveRuntimeMode } from './runtime-mode.js';
import { resolveRenderCap, DEFAULT_RENDER_CAP } from './render-cap.js';
import { createFpsSampler } from './fps-meter.js';
import { resolveLang, t, resolveLocale } from './i18n.js';

// Active language (German default, French via ?lang=fr) + its number/date locale.
const LANG = typeof window !== 'undefined' ? resolveLang(window.location.search) : 'de';
const LOCALE = resolveLocale(LANG);

// Runtime feature flags from the URL + host. ?dev / localhost = dev mode;
// ?simulate = demo-simulation mode (works in a production build, e.g. the CF
// Pages demo). Both lift the once-per-session plant limit and enable the
// ?simulatePennants load simulation. See js/runtime-mode.js.
const RUNTIME_MODE = typeof window !== 'undefined'
    ? resolveRuntimeMode(window.location.search, { hostname: window.location.hostname })
    : resolveRuntimeMode('');
const DEV_MODE = RUNTIME_MODE.devMode;
const UNLIMITED_PLANTS = RUNTIME_MODE.unlimitedPlants;
// Demo mode: the public CF Pages demo (hopp-demo.pages.dev) or any dev/simulate
// load. Enables demo-only testing affordances (short glow window + a live
// glow-duration slider) that must never appear in the real Blick embed.
const DEMO_MODE = (typeof window !== 'undefined' && window.location.hostname.includes('hopp-demo'))
    || RUNTIME_MODE.simulateMode
    || DEV_MODE;
// Fresh-pole glow + sky-beacon window (seconds). Production keeps the 30-min
// default; the demo starts at a short, observable 10s and is retunable live.
const DEMO_FRESH_GLOW_WINDOW_SECONDS = 10;
if (UNLIMITED_PLANTS) {
    clearSessionPlanted();
    console.log('[hopp] plant limit bypassed (dev/simulate mode)');
}
function isSessionPlanted(...args) { return UNLIMITED_PLANTS ? false : _isSessionPlanted(...args); }
function markSessionPlanted(...args) { if (!UNLIMITED_PLANTS) _markSessionPlanted(...args); }

// Cheap device-capability signals for the render-cap tier (see js/render-cap.js).
function detectDeviceEnv() {
    const nav = typeof navigator !== 'undefined' ? navigator : {};
    const coarsePointer = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
    return {
        coarsePointer,
        deviceMemory: nav.deviceMemory,
        hardwareConcurrency: nav.hardwareConcurrency,
    };
}

import { getOwnerToken, recordPennantOwnership, isOwnedPennant } from './owner-token.js';
import { initCameraControls, update as updateCameraControls, focusOnThenReturn, computeVisibleCenterSafeBottomPx, setInitialPose, returnToInitialView, cancelAllAnimations, playIntroSweep } from './camera-controls.js';
import { createGlobalWindFromCameraPose, resolvePennantRotation } from './wind.js';

// Every pennant plants at a fixed, uniform pole height. The old Bronze/Silver/
// Gold hoist minigame that grew the pole is gone; scale 1.0 is the pole at its
// designed height (POLE_HEIGHT).
const DEFAULT_POLE_HEIGHT_SCALE = 1.0;

let renderer = null;
let scene = null;
let camera = null;
let pennantSystem = null;
let fieldScene = null;
let grassMesh = null;
let flagAtlas = null;
let totalPennantCount = 0;
// Authoritative DB total from GET /stats (null until first poll / offline). The
// displayed counter is decoupled from the rendered set: it shows this real total
// when known, falling back to the local optimistic count otherwise.
let realPennantTotal = null;
// The user's own just-planted pennants not yet reflected in the last /stats poll.
// Adds a transient optimistic bump to the displayed counter; cleared on each poll
// so the number stays true and can't drift upward. See resolveDisplayedCount.
let pendingLocalPlants = 0;
let latestPennantTime = null;
let teamCounts = new Map();
let lastSunUpdateMs = 0;
let globalWind = null;
// Device-tiered cap on how many pennants we render at once (desktop full, mobile
// smaller). Set from config + device signals in init(); DB + counter still show
// the real total, and older pennants are evicted (LRU) rather than dropped.
let MAX_RENDERED_PENNANTS = DEFAULT_RENDER_CAP.desktop;
// Dev-only ceiling for URL load simulations (?simulatePennants=N).
const DEV_SIM_MAX_PENNANTS = 500000;
// Spare instance slots kept free above a simulated field so a tester can still
// plant by hand after ?simulatePennants has filled the buffer.
const SIM_MANUAL_PLANT_HEADROOM = 200;

function stableUnitHash(value) {
    let h = 2166136261;
    const input = String(value ?? '');
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return (h >>> 0) / 4294967296;
}

function phaseForPennant(pennant) {
    return stableUnitHash(`${pennant.id}:${pennant.team}:${pennant.x}:${pennant.y}`) * Math.PI * 2;
}

function windSeedForPennant(pennant) {
    return `${pennant.id}:${pennant.team}:${pennant.x}:${pennant.y}`;
}

function visualRotationForPennant(pennant) {
    return resolvePennantRotation(globalWind?.rotation ?? 0, {
        storedRotation: pennant.rotation,
        seed: windSeedForPennant(pennant),
    });
}

function plantedAtForShader(createdAt) {
    const nowSeconds = performance.now() / 1000;
    if (!createdAt) return nowSeconds;
    const createdMs = Date.parse(createdAt);
    if (!Number.isFinite(createdMs)) return nowSeconds;
    const ageSeconds = Math.max(0, (Date.now() - createdMs) / 1000);
    return nowSeconds - Math.min(ageSeconds, 30);
}

// Real wall-clock age (seconds) of a server pennant, for the pole's fresh-glow
// window. Unlike plantedAtForShader this is NOT clamped — a pole created 12 min
// ago must read as 12 min old so it glows for its remaining window (and stops
// once past 30 min), including other fans' poles and across reloads.
function poleAgeSeconds(createdAt) {
    if (!createdAt) return 0;
    const createdMs = Date.parse(createdAt);
    if (!Number.isFinite(createdMs)) return 0;
    return Math.max(0, (Date.now() - createdMs) / 1000);
}

function lookupFlagIndex(teamCode) {
    if (!flagAtlas) return 0;
    const idx = flagAtlas.indexByCode.get(teamCode);
    if (idx === undefined) {
        console.warn(`No flag atlas entry for team code: ${teamCode}`);
        return 0;
    }
    return idx;
}

function isWebGLSupported() {
    try {
        const canvas = document.createElement('canvas');
        return !!(window.WebGLRenderingContext && (canvas.getContext('webgl2') || canvas.getContext('webgl')));
    } catch {
        return false;
    }
}

// Demo-only overlay: a live slider that retunes the fresh-pole glow + sky-beacon
// duration (seconds) instantly, so the glow/fade can be dialled in on
// hopp-demo.pages.dev without a rebuild. Never shown in the real Blick embed.
// Demo-only FPS readout (top-left), updated from the animate() loop. Never shown
// in the real Blick embed — a live perf gauge while testing large fields.
const fpsSampler = createFpsSampler({ intervalMs: 500 });
let fpsEl = null;
function buildDemoFpsMeter() {
    if (document.getElementById('demo-fps-meter')) return;
    fpsEl = document.createElement('div');
    fpsEl.id = 'demo-fps-meter';
    fpsEl.style.cssText = [
        'position:fixed', 'left:12px', 'top:12px', 'z-index:9999',
        'font:600 12px/1.2 ui-monospace,SFMono-Regular,Menlo,monospace', 'color:#fff',
        'background:rgba(0,0,0,0.55)', 'padding:5px 8px', 'border-radius:6px',
        'backdrop-filter:blur(4px)', 'user-select:none', 'pointer-events:none',
        'min-width:56px', 'text-align:center',
    ].join(';');
    fpsEl.textContent = '– fps';
    document.body.appendChild(fpsEl);
}

function updateFpsMeter(nowMs) {
    const reported = fpsSampler.sample(nowMs);
    if (reported !== null && fpsEl) fpsEl.textContent = `${reported} fps`;
}

function buildDemoGlowControl(initialSeconds) {
    if (document.getElementById('demo-glow-control')) return;
    const panel = document.createElement('div');
    panel.id = 'demo-glow-control';
    panel.style.cssText = [
        'position:fixed', 'left:12px', 'bottom:12px', 'z-index:9999',
        'font:12px/1.4 system-ui,sans-serif', 'color:#fff',
        'background:rgba(0,0,0,0.55)', 'padding:8px 10px', 'border-radius:8px',
        'backdrop-filter:blur(4px)', 'user-select:none', 'pointer-events:auto',
    ].join(';');

    const fmt = (s) => (s < 60 ? `${s}s` : `${Math.floor(s / 60)}m${s % 60 ? ` ${s % 60}s` : ''}`);

    const label = document.createElement('label');
    label.textContent = `Beacon glow: ${fmt(initialSeconds)}`;
    label.style.cssText = 'display:block;margin-bottom:4px';

    const slider = document.createElement('input');
    slider.type = 'range';
    slider.min = '1';
    slider.max = '1200'; // up to 20 minutes
    slider.step = '1';
    slider.value = String(initialSeconds);
    slider.style.cssText = 'width:150px;display:block';
    slider.addEventListener('input', () => {
        const seconds = Number(slider.value);
        const applied = setFreshGlowWindow(pennantSystem, seconds);
        label.textContent = `Beacon glow: ${fmt(applied)}`;
    });

    panel.appendChild(label);
    panel.appendChild(slider);
    document.body.appendChild(panel);
}

function updateCounter() {
    const timeEl = document.getElementById('counter-time');
    const countEl = document.getElementById('counter-count');
    if (!countEl) return;

    const displayedCount = resolveDisplayedCount(realPennantTotal, {
        localFallback: totalPennantCount,
        pendingLocalPlants,
    });
    const countText = displayedCount.toLocaleString(LOCALE);
    countEl.textContent = countText;
    // Length-aware size (in cqw, so it tracks the flag width at every breakpoint):
    // long numbers like "200.000" shrink to stay inside the flag.
    const len = countText.length;
    const cqw = len <= 3 ? 28 : len <= 5 ? 22 : len <= 6 ? 19 : len <= 7 ? 16 : len <= 9 ? 13 : 11;
    countEl.style.fontSize = `${cqw}cqw`;

    if (timeEl) {
        if (latestPennantTime) {
            const date = new Date(latestPennantTime);
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            timeEl.textContent = t('counter.time', LANG, { time: `${hours}:${minutes}` });
        } else {
            timeEl.textContent = '';
        }
    }
}

function recordTeamPennant(teamCode) {
    incrementTeamCount(teamCounts, teamCode);
}

function updateLatestTime(pennant) {
    if (!pennant.created_at) return;
    const t = new Date(pennant.created_at);
    if (!latestPennantTime || t > new Date(latestPennantTime)) {
        latestPennantTime = pennant.created_at;
    }
}

function getPennantSimulationParams() {
    // The ?simulatePennants load simulation runs under the Vite dev server and in
    // any build launched with ?simulate / ?dev (so it works on the CF Pages demo).
    if (!import.meta.env?.DEV && !RUNTIME_MODE.simulationEnabled) return null;
    return parsePennantSimulationParams(window.location.search, { maxCount: DEV_SIM_MAX_PENNANTS });
}

function plantFromServerPennant(p) {
    const team = getTeam(p.team);
    if (!team) {
        console.warn(`Unknown team code in server pennant: ${p.team}`);
        return false;
    }
    const alreadyRendered = pennantSystem.idIndex?.has(p.id);
    const visualRotation = visualRotationForPennant(p);
    // Every pennant renders at the same uniform pole height. Flag size is always
    // constant; flag position is derived from pole-top.
    // Server stores (x, y) as the planar pitch position. In Three.js the
    // second ground axis is world z, so we keep mapping server.y to scene.z.
    const idx = addPennant(pennantSystem, {
        id: p.id,
        x: p.x,
        y: p.surface_y ?? 0,
        z: p.y,
        rotation: visualRotation,
        primary: hexToRgb(team.primary),
        secondary: hexToRgb(team.secondary),
        plantedAt: plantedAtForShader(p.created_at),
        ageSeconds: poleAgeSeconds(p.created_at),
        flagIndex: lookupFlagIndex(p.team),
        phase: phaseForPennant(p),
        teamCode: p.team,
        poleHeightScale: DEFAULT_POLE_HEIGHT_SCALE,
    });
    if (idx >= 0) updatePennantMatrices(pennantSystem, idx);
    if (!alreadyRendered) recordTeamPennant(p.team);
    return true;
}

// Apply the language to the document + static markup (title, meta, header).
// German literals stay in index.html as the no-JS fallback.
function applyStaticI18n() {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = LANG;
    document.title = t('meta.title', LANG);
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', t('meta.description', LANG));
    const title = document.getElementById('title');
    if (title) {
        title.textContent = t('title.wm2026', LANG);
        title.setAttribute('aria-label', t('title.wm2026', LANG));
    }
}

async function init() {
    try {
        applyStaticI18n();
        await loadConfig();
        await loadTeams();

        if (!isWebGLSupported()) {
            showWebGLError();
            return;
        }

        initApiClient();
        teamCounts = createTeamCounts(getAllTeams());

        const container = document.getElementById('pennant-container');
        if (!container) throw new Error('#pennant-container not found in DOM');
        const viewportAspect = container.clientWidth / container.clientHeight;

        // Three.js scene + renderer — the procedural blue sky drives the
        // backdrop colour; a soft sky-blue clear keeps the brief pre-render
        // moment from flashing black.
        scene = new THREE.Scene();
        scene.background = new THREE.Color(0xb8d0e8);
        scene.fog = createFieldFog();

        // One authoritative sun vector drives the visible flare, pitch sky,
        // shader lighting, and pennant shadow direction.
        const sunDirection = getSunDirection(new Date());
        const initialCameraPose = getInitialCameraPose(sunDirection, {
            fovDeg: INITIAL_CAMERA_FOV_DEG,
            aspect: viewportAspect,
        });
        globalWind = createGlobalWindFromCameraPose(initialCameraPose);
        lastSunUpdateMs = performance.now();

        // Far plane has to clear the procedural sky dome and oversized pitch.
        camera = new THREE.PerspectiveCamera(
            INITIAL_CAMERA_FOV_DEG,
            viewportAspect,
            0.1,
            10_000,
        );
        // Low, flatter broadcast-style cam: enough height for placement
        // context, but closer to pitch level so dense flag clusters read
        // like a real field view.
        camera.position.set(
            initialCameraPose.position.x,
            initialCameraPose.position.y,
            initialCameraPose.position.z,
        );
        camera.lookAt(
            initialCameraPose.target.x,
            initialCameraPose.target.y,
            initialCameraPose.target.z,
        );

        renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
        renderer.setSize(container.clientWidth, container.clientHeight);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        // Keep ACES for the shader-lit pitch and pennants; this exposure
        // keeps the sky readable while flags still pop against the green.
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 0.4;
        container.appendChild(renderer.domElement);

        // Daylight pitch + sky; pennants plant flat on the green plane.
        fieldScene = createFieldScene({ sunDirection, aspect: viewportAspect });
        scene.add(fieldScene);
        grassMesh = createGrassBillboards({ cameraPose: initialCameraPose });
        scene.add(grassMesh);
        // Apply the real time-of-day lighting now so the first rendered frame
        // already shows dusk/night colours when applicable, instead of popping
        // from the seed daylight palette a few seconds later.
        updateSunFromClock(true);
        const pitch = getPitchMesh(fieldScene);

        // Build the flag-image atlas before the pennant system so the shader
        // gets a real texture from the first frame; failed PNGs fall back to
        // each team's primary colour painted into the cell.
        flagAtlas = await buildFlagAtlas(getAllTeams(), {
            anisotropy: renderer.capabilities.getMaxAnisotropy?.() ?? 4,
        });

        // Device-tiered render cap: desktop renders more than a phone. Config can
        // override the tier values; the DB + counter still report the real total.
        const pennantsCfg = getPennantsConfig();
        MAX_RENDERED_PENNANTS = resolveRenderCap(detectDeviceEnv(), {
            desktop: pennantsCfg.renderCapDesktop ?? DEFAULT_RENDER_CAP.desktop,
            mobile: pennantsCfg.renderCapMobile ?? DEFAULT_RENDER_CAP.mobile,
        });

        // In dev, a URL simulation (?simulatePennants=N) can request far more
        // pennants than the normal render cap — size the instanced buffers to fit.
        const simulation = getPennantSimulationParams();
        const maxPennantInstances = simulation
            ? Math.max(MAX_RENDERED_PENNANTS, simulation.count) + SIM_MANUAL_PLANT_HEADROOM
            : MAX_RENDERED_PENNANTS;

        // Pennant rendering system — share the same sun direction so flags
        // and poles light from the same direction as the pitch shader.
        pennantSystem = createPennantSystem({
            maxInstances: maxPennantInstances,
            sunDirection,
            flagAtlas,
            // Short glow window on the demo so the fresh-pole glow + beacon fade
            // is observable in seconds rather than the 30-minute production window.
            // The demo slider retunes this live.
            freshGlowWindowSeconds: DEMO_MODE ? DEMO_FRESH_GLOW_WINDOW_SECONDS : undefined,
        });
        scene.add(pennantSystem.shadowMesh, pennantSystem.poleMesh, pennantSystem.flagMesh, pennantSystem.beaconMesh);
        setFlutterIntensity(pennantSystem, getPennantsConfig().flutterIntensity ?? 0.6);
        if (DEMO_MODE) {
            buildDemoGlowControl(DEMO_FRESH_GLOW_WINDOW_SECONDS);
            buildDemoFpsMeter();
        }
        // Expose for in-browser debugging — dev server or demo mode (never the
        // real Blick embed). Lets the demo be inspected/smoke-tested live.
        if (import.meta.env?.DEV || DEMO_MODE) {
            window.__hopp = {
                scene, camera, renderer, pennantSystem, flagAtlas, globalWind,
                focusOnThenReturn, addPennant,
            };
        }

        // Camera pan/zoom controls — pitch is huge; bound to the area
        // around the centre markings where pennants are likely to plant.
        initCameraControls(camera, renderer.domElement, {
            initialTarget: initialCameraPose.target,
            minDistance: 12,
            maxDistance: 180,
            maxPolarAngle: Math.PI * 0.78,
            panBounds: { minX: -145, maxX: 145, minZ: -190, maxZ: 65 },
        });
        // Snapshot the page-load pose so returnToInitialView can animate back
        // to it after every plant-and-minigame sequence.
        setInitialPose(initialCameraPose.position, initialCameraPose.target);

        // Tap-to-plant interaction is wired in initPlantInteraction() below.

        // Initial fetch — populate the canvas with everything that's already there.
        // In dev, URL params can replace backend data with deterministic load
        // simulations: ?simulatePennants=1000&simulateSwissShare=0.6
        const initialFetchLimit = import.meta.env?.DEV
            ? maxPennantInstances
            : (getPennantsConfig().initialFetchLimit ?? 500);
        const recent = simulation
            ? generateSimulatedPennants(getAllTeams(), simulation)
            : await fetchRecentPennants(initialFetchLimit);
        initializeKnownPennants(recent);
        for (const p of recent) {
            if (plantFromServerPennant(p)) updateLatestTime(p);
        }
        totalPennantCount = summarizeTeamCounts(teamCounts, getAllTeams()).total;
        updateCounter();
        if (import.meta.env?.DEV && simulation) {
            console.info(
                `Simulated ${simulation.count} pennants; ` +
                `${Math.round(simulation.swissShare * 100)}% ${simulation.homeTeam}`,
            );
        }

        // Real DB total for the counter — polled from /stats, decoupled from the
        // rendered set so the number stays true even when we cap what we draw.
        // Simulation mode has no backend, so it keeps the local (simulated) count.
        if (!simulation) {
            const pollStats = async () => {
                const total = await fetchStats();
                if (total !== null) {
                    realPennantTotal = total;
                    // Stats now includes any prior own-plants — clear the optimistic bump.
                    pendingLocalPlants = 0;
                    updateCounter();
                }
            };
            await pollStats();
            const statsIntervalMs = getPennantsConfig().statsPollIntervalMs ?? 15000;
            setInterval(pollStats, statsIntervalMs);
        }

        // Polling for new pennants from other visitors — RENDER only. The counter
        // is driven by the authoritative /stats poll above, not by this 4s poll:
        // incrementing here double-counted the user's own plant when the poll
        // re-observed it before its POST registered the server id (see hopp-61zd).
        if (!simulation) {
            await subscribeToPennants((p) => {
                if (plantFromServerPennant(p)) {
                    updateLatestTime(p);
                }
            });
        }

        showOnboarding();
        initPlantInteraction();

        window.addEventListener('resize', onResize);
        animate();

        // Cinematic intro: sweep the panorama, then settle on the midfield.
        if (!new URLSearchParams(window.location.search).has('nointro')) {
            playIntroSweep();
        }
    } catch (error) {
        console.error('Failed to initialize Hopp:', error);
        showErrorState();
    }
}

// ─── Tap-to-plant ──────────────────────────────────────────────────────────

// A pointer that presses and releases on nearly the same spot is a plant tap;
// anything with meaningful travel (or a long hold) is a camera pan/orbit and is
// left to the camera controls.
const TAP_MOVE_TOLERANCE_PX = 8;
const TAP_MAX_DURATION_MS = 500;
let tapCandidate = null; // { pointerId, x, y, time }

function initPlantInteraction() {
    const canvas = renderer.domElement;
    canvas.addEventListener('pointerdown', onCanvasPointerDown);
    canvas.addEventListener('pointerup', onCanvasPointerUp);
    canvas.addEventListener('pointercancel', () => { tapCandidate = null; });
}

function onCanvasPointerDown(event) {
    tapCandidate = {
        pointerId: event.pointerId,
        x: event.clientX,
        y: event.clientY,
        time: performance.now(),
    };
}

function onCanvasPointerUp(event) {
    const candidate = tapCandidate;
    tapCandidate = null;
    if (!candidate || candidate.pointerId !== event.pointerId) return;

    const moved = Math.hypot(event.clientX - candidate.x, event.clientY - candidate.y);
    const elapsed = performance.now() - candidate.time;
    // Travelled too far or held too long → the camera handled it, not a plant.
    if (moved > TAP_MOVE_TOLERANCE_PX || elapsed > TAP_MAX_DURATION_MS) return;

    plantAt(event.clientX, event.clientY);
}

function plantAt(clientX, clientY) {
    if (isSessionPlanted()) {
        showSessionLimitToast();
        return;
    }

    // Switzerland is the only team; the registry holds its single entry.
    const teamData = getAllTeams()[0];
    if (!teamData) return;

    const pitch = getPitchMesh(fieldScene);
    const world = screenToWorld(clientX, clientY, camera, renderer.domElement, pitch);
    if (!world) return; // tap landed off the pitch (e.g. the sky) — ignore

    const previewId = `preview-${crypto.randomUUID()}`;
    const rotation = (Math.random() - 0.5) * 0.17; // faint ~5deg jitter around the steady wind
    const visualRotation = resolvePennantRotation(globalWind?.rotation ?? 0, {
        storedRotation: rotation,
        seed: previewId,
    });

    const index = addPennant(pennantSystem, {
        id: previewId,
        x: world.x,
        y: world.y,
        z: world.z,
        rotation: visualRotation,
        primary: hexToRgb(teamData.primary),
        secondary: hexToRgb(teamData.secondary),
        plantedAt: performance.now() / 1000,
        flagIndex: lookupFlagIndex(teamData.code),
        phase: stableUnitHash(previewId) * Math.PI * 2,
        teamCode: teamData.code,
        swingBoost: 1.0,
        poleHeightScale: DEFAULT_POLE_HEIGHT_SCALE,
        // The user's own plant is pinned so the render cap never evicts it.
        pinned: true,
    });
    if (index === -1) return;

    commitPlant({ previewIndex: index, previewId, team: teamData.code, rotation });
}

function commitPlant(prevDrag) {
    const { previewIndex, previewId, team, rotation } = prevDrag;
    const data = pennantSystem.instanceData[previewIndex];
    const worldPos = { x: data.x, y: data.y, z: data.z };

    // Settle the pennant at its uniform planted height. The lively drag-preview
    // flutter boost calms to the ambient idle wave.
    setPennantSwingBoost(pennantSystem, previewIndex, 0);
    data.swingAngle = 0;
    data.flagSwingOffset = 0;
    setPennantPoleHeightScale(pennantSystem, previewIndex, DEFAULT_POLE_HEIGHT_SCALE);
    updatePennantMatrices(pennantSystem, previewIndex);

    // Persist the plant to the backend.
    const ownerToken = getOwnerToken();
    createPennant({
        x: worldPos.x,
        y: worldPos.z,
        team,
        rotation,
        ownerToken,
    }).then((saved) => {
        if (saved?.id) {
            // Rename in place so the render-cap recency + pinned bookkeeping
            // follows the id from the optimistic preview to the server id.
            renamePennantId(pennantSystem, previewId, saved.id);
            recordPennantOwnership(saved.id, saved.owner_token || ownerToken);
            initializeKnownPennants([{ id: saved.id }]);
        }
    }).catch((error) => {
        console.error('Plant POST failed:', error);
    });

    showFocusMessage(t('focus.plantConfirm', LANG), { visibleMs: 1500 });
    markSessionPlanted();
    recordTeamPennant(team);
    // Offline/sim fallback count; online this is ignored in favour of /stats.
    totalPennantCount++;
    // Online optimistic bump — cleared at the next /stats poll so it can't inflate.
    pendingLocalPlants++;
    updateCounter();

    // Briefly zoom in on the freshly-planted pennant, then animate back to the
    // initial view on its own. Aim at the flag's centre, but correct for the
    // square (1:1) canvas overflowing a landscape host — safeBottomPx raises the
    // look-at so the flag lands at the centre of the VISIBLE viewport, not the
    // canvas optical centre (which sits low on wide hosts). Works desktop+mobile.
    const canvasRect = renderer.domElement.getBoundingClientRect();
    const safeBottomPx = computeVisibleCenterSafeBottomPx({
        canvasTop: canvasRect.top,
        canvasHeight: canvasRect.height,
        viewportHeight: window.innerHeight || canvasRect.height,
    });
    focusOnThenReturn(worldPos.x, worldPos.z, {
        zoomDistance: 15,
        targetY: PENNANT_FLAG_FOCUS_Y,
        safeBottomPx,
        minY: 0.4,
        focusDurationMs: 1200,
        holdMs: 1400,
        returnDurationMs: 1500,
    });
}

function animate() {
    requestAnimationFrame(animate);
    const nowMs = performance.now();
    updateSunFromClock();
    const nowSeconds = nowMs / 1000;
    updateFlutter(pennantSystem, nowSeconds);
    updateGrassWind(grassMesh, nowSeconds);
    updateCameraControls();

    renderer.render(scene, camera);
    if (DEMO_MODE) updateFpsMeter(nowMs);
}

function getClockDate() {
    // ?simHour=22 (or ?simHour=22.5) overrides the wall clock for the celestial
    // calculation only, so dusk/night can be checked without waiting for it.
    if (typeof window === 'undefined') return new Date();
    const params = new URLSearchParams(window.location.search ?? '');
    const raw = params.get('simHour');
    if (raw == null) return new Date();
    const hours = Number.parseFloat(raw);
    if (!Number.isFinite(hours)) return new Date();
    const now = new Date();
    const wholeHour = Math.floor(hours);
    const minutes = Math.round((hours - wholeHour) * 60);
    now.setHours(wholeHour, minutes, 0, 0);
    return now;
}

function updateSunFromClock(force = false) {
    const nowMs = performance.now();
    if (!force && lastSunUpdateMs !== 0 && nowMs - lastSunUpdateMs < 5_000) return;
    lastSunUpdateMs = nowMs;

    const state = getCelestialState(getClockDate());
    // Compose the moon into the framed view: aim it down the live camera forward,
    // just above the ridge (night only), so the thin crescent is actually visible
    // rather than sitting at its true off-screen / below-horizon position. The
    // crescent *shape* stays true to the phase; only the placement is staged.
    if (camera && state.moonDirection) {
        const fwd = new THREE.Vector3();
        camera.getWorldDirection(fwd);
        const az = Math.atan2(fwd.x, fwd.z) + THREE.MathUtils.degToRad(6);
        const elevDeg = state.dayFactor < 0.5 ? 11 : -20;
        const phi = THREE.MathUtils.degToRad(90 - elevDeg);
        state.moonDirection.setFromSphericalCoords(1, phi, az).normalize();
    }
    if (fieldScene) setFieldCelestialState(fieldScene, state);
    if (pennantSystem) setPennantSunDirection(pennantSystem, state.primaryDirection);
    if (grassMesh) setGrassLighting(grassMesh, state);
}

let focusMessageHideTimer = null;
let focusMessageRemoveTimer = null;

function showFocusMessage(text, { visibleMs = 3000 } = {}) {
    const el = document.getElementById('focus-message');
    if (!el) return;

    if (focusMessageHideTimer) clearTimeout(focusMessageHideTimer);
    if (focusMessageRemoveTimer) clearTimeout(focusMessageRemoveTimer);

    el.textContent = text;
    el.classList.remove('hidden');
    // Next frame so the transition picks up the opacity change.
    requestAnimationFrame(() => el.classList.add('visible'));

    focusMessageHideTimer = setTimeout(() => {
        el.classList.remove('visible');
        focusMessageRemoveTimer = setTimeout(() => {
            el.classList.add('hidden');
        }, 500);
    }, visibleMs);
}

function onResize() {
    const container = document.getElementById('pennant-container');
    if (!container) return;
    camera.aspect = container.clientWidth / container.clientHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(container.clientWidth, container.clientHeight);
}

let toastTimer = null;
function showSessionLimitToast() {
    let toast = document.getElementById('session-limit-toast');
    if (!toast) {
        toast = document.createElement('div');
        toast.id = 'session-limit-toast';
        toast.textContent = t('toast.sessionLimit', LANG);
        document.getElementById('app').appendChild(toast);
    }
    toast.classList.remove('hidden');
    toast.classList.add('visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
        toast.classList.remove('visible');
        toast.classList.add('hidden');
    }, 2500);
}

// ─── Overlays ────────────────────────────────────────────────────────────

function showOnboarding() {
    const onboardingConfig = getOnboardingConfig();
    if (!onboardingConfig?.enabled) return;
    if (isSessionPlanted()) return;

    const overlay = document.getElementById('onboarding-overlay');
    const text = document.getElementById('onboarding-text');
    if (!overlay || !text) return;

    text.textContent = t('onboarding.text', LANG);
    overlay.classList.remove('hidden');

    const dismiss = () => {
        overlay.classList.add('fade-out');
        setTimeout(() => {
            overlay.classList.add('hidden');
            overlay.classList.remove('fade-out');
        }, 500);
        overlay.removeEventListener('click', dismiss);
        overlay.removeEventListener('touchstart', dismiss);
    };
    overlay.addEventListener('click', dismiss);
    overlay.addEventListener('touchstart', dismiss);
    setTimeout(dismiss, onboardingConfig.dismissAfterMs ?? 5000);
}

function showWebGLError() {
    const app = document.getElementById('app');
    app.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;height:100%;color:rgba(255,255,255,0.6);font-size:1rem;text-align:center;padding:2rem;flex-direction:column;gap:1rem;">
            <p>${t('error.webglNotSupported', LANG)}</p>
            <p style="font-size:0.85rem;color:rgba(255,255,255,0.4);">${t('error.webglHint', LANG)}</p>
        </div>`;
}

function showErrorState() {
    const msg = t('error.loadFailed', LANG);
    const app = document.getElementById('app');
    app.innerHTML = `
        <div style="display:flex;align-items:center;justify-content:center;height:100%;color:rgba(255,255,255,0.5);font-size:1rem;text-align:center;padding:2rem;">
            <p>${msg}</p>
        </div>`;
}

document.addEventListener('DOMContentLoaded', init);
